import { useRef, useState } from 'react'

import type { FetchResult } from '@apollo/client'
import { useApolloClient } from '@apollo/client'
import { i18n } from '@lingui/core'

import type { FlowQuery } from '@twisto/api-graphql'
import { FlowDocument } from '@twisto/api-graphql'

import { useSubmittingWrapper } from '../components/flow-hooks/submitting-wrapper.hook'
import { useFlow } from '../flow/flow.context'

import type { RlcMutationData } from './rlc.helpers'
import {
  getRlcErrorMessage,
  getRlcNonFieldError,
  getRlcPayloads,
} from './rlc.helpers'

export const rlcRequestOptions = {
  context: { overrideNonFieldErrorHandler: true },
}

export const useRlcAction = () => {
  const { sessionId } = useFlow()

  return useRlcSessionAction(sessionId)
}

export const useRlcSessionAction = (sessionId: string) => {
  const client = useApolloClient()
  const submittingWrapper = useSubmittingWrapper()
  const inFlight = useRef(false)
  const needsRefresh = useRef(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string>()
  const [requiresRefresh, setRequiresRefresh] = useState(false)

  const refresh = async () => {
    try {
      const { data } = await client.query<FlowQuery>({
        ...rlcRequestOptions,
        query: FlowDocument,
        variables: { auth: { sessionId } },
        fetchPolicy: 'network-only',
      })
      if (!data.flow) {
        throw new Error('Flow unavailable')
      }
      needsRefresh.current = false
      setRequiresRefresh(false)

      return true
    } catch {
      needsRefresh.current = true
      setRequiresRefresh(true)
      setError((previous) => previous || i18n._('error.general.description'))

      return false
    }
  }

  const retryRefresh = async () => {
    if (inFlight.current) {
      return
    }
    inFlight.current = true
    setPending(true)
    if (await refresh()) {
      setError(undefined)
    }
    inFlight.current = false
    setPending(false)
  }

  const run = async <T extends RlcMutationData>(
    action: () => Promise<FetchResult<T>>
  ) => {
    if (inFlight.current || needsRefresh.current) {
      return false
    }
    inFlight.current = true
    setPending(true)
    setError(undefined)
    try {
      const result = await action()
      const nonFieldError = getRlcNonFieldError(result.data)
      const payloads = getRlcPayloads(result.data)
      if (
        nonFieldError ||
        !payloads.length ||
        result.errors?.length ||
        payloads.some((payload) => !payload.success && !payload.errors?.length)
      ) {
        setError(getRlcErrorMessage(nonFieldError?.message))
        await refresh()

        return false
      }

      return payloads.every(
        (payload) => !payload.errors?.length && !!payload.success
      )
    } catch {
      await refresh()

      return false
    } finally {
      inFlight.current = false
      setPending(false)
    }
  }

  return {
    run: <T extends RlcMutationData>(action: () => Promise<FetchResult<T>>) =>
      inFlight.current || needsRefresh.current
        ? Promise.resolve(false)
        : submittingWrapper(run(action)),
    error,
    pending,
    requiresRefresh,
    retryRefresh: () =>
      inFlight.current ? Promise.resolve() : submittingWrapper(retryRefresh()),
  }
}
